import { MessageCircle, ArrowRight, Zap } from "lucide-react";
import { useLang } from "../context/LangContext";
import { waLink } from "../constants";
import { Reveal } from "./Reveal";

export const RateCompare = () => {
  const { t } = useLang();
  const c = t.compare;

  return (
    <section id="compare" className="relative py-20 lg:py-28">
      <div className="pointer-events-none absolute left-[-140px] top-1/4 h-[360px] w-[360px] rounded-full bg-blue-700/10 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400">{c.tag}</span>
          <h2 className="mt-4 font-display text-3xl font-black text-white sm:text-4xl lg:text-5xl">{c.title}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{c.desc}</p>
        </Reveal>

        <div className="relative mt-14 grid gap-6 md:grid-cols-2 lg:mt-16">
          {/* VS badge */}
          <div className="absolute left-1/2 top-1/2 z-10 hidden h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-navy-900 font-display text-sm font-black text-white shadow-[0_0_30px_rgba(59,130,246,0.4)] md:flex">
            VS
          </div>

          {/* standard */}
          <Reveal delay={0.05}>
            <div
              data-testid="standard-rate-card"
              className="glass h-full rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{c.standardTag}</span>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-6xl font-black text-slate-500 sm:text-7xl">{c.standardRate}</span>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-400">{c.standardLabel}</p>
              <div className="my-7 h-px bg-white/5" />
              <ul className="space-y-3.5">
                {c.standardPoints.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-400">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* exclusive */}
          <Reveal delay={0.15}>
            <div
              data-testid="exclusive-rate-card"
              className="glow-card relative h-full overflow-hidden rounded-3xl bg-gradient-to-b from-blue-950/60 via-navy-900/90 to-navy-950 p-8 transition-transform duration-500 hover:-translate-y-1"
            >
              <div className="animate-pulse-glow pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-blue-500/25 blur-[80px]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">{c.vipTag}</span>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="text-sm font-semibold text-slate-400">{c.vipPrefix}</span>
                <span className="text-gradient font-display text-6xl font-black sm:text-7xl">{c.vipRate}</span>
              </div>
              <p className="mt-2 text-sm font-semibold text-blue-300">{c.vipLabel}</p>
              <div className="my-7 h-px bg-gradient-to-r from-blue-500/40 to-transparent" />
              <ul className="space-y-3.5">
                {c.vipPoints.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                    <Zap className="h-4 w-4 shrink-0 text-blue-400" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-12 text-center">
          <p className="mx-auto max-w-xl text-sm text-slate-500">{c.foot}</p>
          <a
            href={waLink(t.contact.msgDefault)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="compare-cta-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-4 text-base font-bold text-white shadow-[0_0_32px_rgba(59,130,246,0.4)] transition-transform duration-300 hover:scale-[1.04]"
          >
            <MessageCircle className="h-5 w-5" />
            {c.cta}
            <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
